import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { autorun, observable } from 'mobx';
import { Flex, VStack, InlineBanner } from '@cad-arc';
import { GroupedVirtuoso } from 'react-virtuoso';
import { BoloQueueView, EventQueueView, QueueDisplayDensityEnum } from '@mark43/cad-api';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';
import { useQueueStore } from '../queues/context/use-queue-store';
import { useCurrentScrollPosition } from '../common/hooks/use-current-scroll-position';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { EventQueueStore } from '../event-queues/store/event-queue-store';
import { useDistanceUpdater } from '../event-queues/use-distance-updater';
import { FrQueueVirtuosoListWrapper } from './fr-queue-virtuoso-list-wrapper';
import { useFrEventsListTab } from './fr-events-list-context';
import { QueueRow } from './fr-queue-row';
import { FrLoadingQueueRows } from './fr-loading-queue-rows';
import { FrCompactQueueRow } from './fr-compact-queue-row';
import { useFrBoloQueueStoreProvider } from './fr-bolo-queue-store-provider';
import { FrBoloCompactQueueRow } from './bolo-list/fr-bolo-compact-queue-row';
import { FrBoloQueueRow } from './bolo-list/fr-bolo-queue-row';
import { BoloQueueRowData, EventQueueRowData } from '../socket/stream-types';
import { QueueStore } from '../queues/store/queue-store';
import {
    useFrBoloQueueFilterState,
    useSetFrBoloQueueFilterState,
} from './state/fr-shared-queue-filters-context';
import { createBoloQueueFilterDirective } from '../bolos/bolo-queues/use-integrated-bolo-queue-store';
import { FrQueueGroupHeader } from './fr-queue-group-header';
import { FR_EVENT_QUEUE_UNMASKED_COLUMNS, FR_BOLO_QUEUE_UNMASKED_COLUMN } from '../common/components/fr-queues/constants';

const computeItemKey = (index: number) => index;

const BOLOS_GROUP_HEADER_NAME = 'BOLOs';

const useRegisterBoloListOptions = (boloQueueStore: QueueStore<BoloQueueView> | null) => {
    const { viewBolos: bolosEnabled } = useEnabledFeatures();

    const { setNumberOfItems } = useSetFrBoloQueueFilterState();
    const { filterDirective, sortingDirective } = useFrBoloQueueFilterState();

    useEffect(() => {
        if (!bolosEnabled || !boloQueueStore) return;
        return autorun(() => {
            setNumberOfItems(boloQueueStore.listStore.data.list.length);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boloQueueStore, bolosEnabled]);

    useEffect(
        function updateQueueFilters() {
            if (!bolosEnabled || !boloQueueStore) return;
            if (!filterDirective) {
                boloQueueStore.listStore.setFilterDirective(undefined);
            }

            const dispatchAreaIds = filterDirective?.dispatchAreaOptions?.map((p) => p.value) ?? [];

            const subdivisionIds = filterDirective?.subdivisionOptions?.map((p) => p.value) ?? [];

            boloQueueStore.listStore.setFilterDirective(
                createBoloQueueFilterDirective({
                    filterQuery: filterDirective?.filterQuery ?? '',
                    dispatchAreaIds,
                    subdivisionIds,
                })
            );
        },
        [
            filterDirective,
            boloQueueStore?.listStore,
            boloQueueStore?.filterQuery,
            bolosEnabled,
            boloQueueStore,
        ]
    );

    useEffect(
        function updateQueueSort() {
            if (!bolosEnabled || !boloQueueStore) return;
            boloQueueStore.setSort(
                sortingDirective?.columnDefinitionName,
                sortingDirective?.sortDirection,
                false,
                { skipDefaultSort: true }
            );
        },
        [sortingDirective, boloQueueStore?.listStore, bolosEnabled, boloQueueStore]
    );
};

const isBoloQueueViewItem = (item: BoloQueueRowData | EventQueueRowData): item is BoloQueueView => {
    return 'boloNumber' in item;
};

export const FrQueue = observer(function FrQueue(): React.ReactElement {
    const eventQueueStore = useQueueStore() as EventQueueStore;
    const { viewBolos: bolosEnabled } = useEnabledFeatures();
    const { tab: selectedTab } = useFrEventsListTab();
    const unitAssignment = useUnitAssignment();
    const boloQueueStore = useFrBoloQueueStoreProvider();
    const [currentScrollPosition, setCurrentScrollPosition] = useCurrentScrollPosition<'div'>();
    useRegisterBoloListOptions(boloQueueStore);
    useDistanceUpdater();

    const { list, groupCounts, groupHeaders } = eventQueueStore.listStore.data;
    const { displayDensity, queueColumns } = eventQueueStore.workspacePanel.queue;
    const isDisplayDensityCompact = displayDensity === QueueDisplayDensityEnum.COMPACT.name;
    const eventId = unitAssignment?.assignedEvent?.eventId;

    const assignedEvent = eventId
        ? (eventQueueStore.listStore.getItemById(eventId) as EventQueueView)
        : undefined;

    let modifiedGroupCounts = groupCounts;
    let modifiedGroupHeaders = groupHeaders;
    let modifiedList: (
        | EventQueueRowData
        | BoloQueueRowData
    )[] = selectedTab === 'BOLO' ? [] : list;
    let baseIndex = 0;
    // this blocked modifying group headers and counts exists because we don't use the QueueStore & ListStore's filtering mechanisms to filter out
    // Pending or Dispatched events in FR, this is because we need the fr event list header to show accurate counts for All, Pending and Dispatched events
    // which doesn't happen if we filter the list
    if (selectedTab !== 'ALL') {
        if (selectedTab === 'UNASSIGNED') {
            const pendingIndex = modifiedGroupHeaders.findIndex((val) => val === 'Pending');
            if (pendingIndex > -1) {
                modifiedGroupHeaders = observable(['Pending']);
                modifiedGroupCounts = observable([groupCounts[pendingIndex]]);
                if (pendingIndex !== 0) {
                    baseIndex = groupCounts[0];
                }
            } else {
                modifiedGroupHeaders = observable(['Pending']);
                modifiedGroupCounts = observable([0]);
            }
        }
        if (selectedTab === 'ASSIGNED') {
            const dispatchedIndex = modifiedGroupHeaders.findIndex((val) => val === 'Dispatched');
            if (dispatchedIndex > -1) {
                modifiedGroupHeaders = observable(['Dispatched']);
                modifiedGroupCounts = observable([groupCounts[dispatchedIndex]]);
                if (dispatchedIndex !== 0) {
                    baseIndex = groupCounts[0];
                }
            } else {
                modifiedGroupHeaders = observable(['Dispatched']);
                modifiedGroupCounts = observable([0]);
            }
        }
    }

    if (eventQueueStore.isError) {
        return (
            <Flex h="100%" justifyContent="center" alignItems="center">
                <InlineBanner
                    status="error"
                    title="An Error Occurred"
                    description={errorOccurredWhile('loading event list data')}
                />
            </Flex>
        );
    }

    if (eventQueueStore.isLoading) {
        return <FrLoadingQueueRows />;
    }

    const showBoloList =
        bolosEnabled && (selectedTab === 'ALL' || selectedTab === 'BOLO');

    const boloQueueColumns = boloQueueStore?.workspacePanel.queue?.queueColumns;

    if (showBoloList && boloQueueStore && !boloQueueStore.isLoading) {
        if (selectedTab === 'BOLO') {
            baseIndex = 0;
            modifiedGroupHeaders = observable([BOLOS_GROUP_HEADER_NAME]);
            modifiedList = boloQueueStore.listStore.data.list;
            if (boloQueueStore.listStore.data.list.length) {
                modifiedGroupCounts = observable([boloQueueStore.listStore.data.list.length]);
            }
        } else {
            modifiedList = observable([...list, ...boloQueueStore.listStore.data.list]);
            if (boloQueueStore.listStore.data.list.length) {
                modifiedGroupHeaders = observable([
                    ...modifiedGroupHeaders,
                    BOLOS_GROUP_HEADER_NAME,
                ]);
                modifiedGroupCounts = observable([
                    ...modifiedGroupCounts,
                    boloQueueStore.listStore.data.list.length,
                ]);
            }
        }
    }

    const boloColumnWidths = boloQueueColumns?.map((col) => `${col.width}px`).join(' ');

    const renderItemContent = (index: number): React.ReactElement | null => {
        const item = modifiedList[index + baseIndex];
        if (!item) return null;

        if (isBoloQueueViewItem(item)) {
            if (!item || !boloQueueColumns) {
                return null;
            }

            if (isDisplayDensityCompact) {
                return (
                    <FrBoloCompactQueueRow
                        index={index}
                        data={item}
                        queueColumns={boloQueueColumns}
                        customGridTemplateColumns={boloColumnWidths}
                    />
                );
            }

            return (
                <FrBoloQueueRow
                    isFirstRow={index === 0}
                    key={item.id}
                    data={item as BoloQueueView}
                />
            );
        } else {
            if (isDisplayDensityCompact) {
                return (
                    <FrCompactQueueRow
                        key={item.id}
                        index={index}
                        data={item as EventQueueView}
                        assignedEvent={assignedEvent}
                        queueColumns={queueColumns}
                    />
                );
            }

            return (
                <QueueRow
                    key={item.id}
                    isFirstRow={index === 0}
                    data={item as EventQueueView}
                    assignedEvent={assignedEvent}
                />
            );
        }
    };

    const renderGroupContent = (groupIndex: number): React.ReactElement | null => {
        const isBolo = modifiedGroupHeaders[groupIndex] === BOLOS_GROUP_HEADER_NAME;
        if (isBolo && (!boloQueueStore || !boloQueueColumns)) return null;

        let columnWidths;
        let densityCompactOptions;

        if (isBolo) {
            // columns widths for events are set on the Virtuoso list as a css variable
            // passing boloQueueColumnWidths for bolo header to replace the event queue column widths
            columnWidths = boloQueueColumns?.map((col) => `${col.width}px`).join(' ');
            densityCompactOptions = {
                isDisplayDensityCompact,
                isSelfCol: false,
                workspaceQueue: boloQueueStore?.workspacePanel.queue,
                unmaskedColumns: FR_BOLO_QUEUE_UNMASKED_COLUMN,
            };
        } else {
            densityCompactOptions = {
                isDisplayDensityCompact,
                isSelfCol: true,
                workspaceQueue: eventQueueStore.workspacePanel.queue,
                unmaskedColumns: FR_EVENT_QUEUE_UNMASKED_COLUMNS,
            };
        }

        return (
            <FrQueueGroupHeader
                groupIndex={groupIndex}
                title={modifiedGroupHeaders[groupIndex]}
                count={modifiedGroupCounts[groupIndex]}
                columnWidths={columnWidths}
                densityCompactOptions={densityCompactOptions}
            />
        );
    };

    const isListEmpty = !modifiedList.length;

    return (
        <VStack
            h="100%"
            backgroundColor={
                isDisplayDensityCompact ? 'var(--arc-colors-surface-foreground)' : 'transparent'
            }
        >
            <Flex w="100%" height="100%" flexDirection="column" position="relative">
                {isListEmpty && selectedTab === 'ALL' && null}
                {isListEmpty && selectedTab !== 'ALL' && (
                    <FrQueueGroupHeader
                        groupIndex={0}
                        densityCompactOptions={{
                            isDisplayDensityCompact,
                            isSelfCol:
                                modifiedGroupHeaders[0] === BOLOS_GROUP_HEADER_NAME ? false : true,
                            workspaceQueue:
                                modifiedGroupHeaders[0] === BOLOS_GROUP_HEADER_NAME
                                    ? boloQueueStore?.workspacePanel.queue
                                    : eventQueueStore.workspacePanel.queue,
                        }}
                        title={modifiedGroupHeaders[0] ?? 'Pending'}
                        count={0}
                        columnWidths={
                            modifiedGroupHeaders[0] === BOLOS_GROUP_HEADER_NAME
                                ? boloQueueColumns?.map((col) => `${col.width}px`).join(' ') ?? ''
                                : queueColumns.map((col) => `${col.width}px`).join(' ')
                        }
                    />
                )}
                {!isListEmpty && (
                    <GroupedVirtuoso
                        key={`fr-events-list-page-${modifiedList.length}`}
                        style={{
                            height: '100%',
                            width: '100%',
                            overflowY: 'auto',
                            backgroundColor: isDisplayDensityCompact
                                ? 'var(--arc-colors-surface-foreground)'
                                : 'transparent',
                            '--columns': queueColumns.map((col) => `${col.width}px`).join(' '),
                        }}
                        groupCounts={modifiedGroupCounts}
                        components={{
                            List: FrQueueVirtuosoListWrapper,
                            Footer: () => {
                                return !isDisplayDensityCompact ? (
                                    <div style={{ paddingBottom: '8px' }} />
                                ) : null;
                            },
                            Item: ({ children, style, ...rest }) => {
                                // In non-compact mode, set minWidth: 0 so rows are constrained to
                                // the list container width to prevent horizontal overflowing.
                                return (
                                    <div
                                        style={{
                                            ...style,
                                            ...(isDisplayDensityCompact ? {} : { minWidth: 0 }),
                                        }}
                                        {...rest}
                                    >
                                        {children}
                                    </div>
                                );
                            },
                        }}
                        computeItemKey={computeItemKey}
                        overscan={200}
                        itemContent={renderItemContent}
                        groupContent={renderGroupContent}
                        onScroll={setCurrentScrollPosition}
                        initialScrollTop={currentScrollPosition}
                    />
                )}
            </Flex>
        </VStack>
    );
});
